import { Injectable } from '@angular/core';
import { AnalyticsEventsMap } from '@shared/utils/constants/analytics-events';
import {
  IAnalyticsBase,
  IAnalyticsEvent,
  IAnalyticsEventLog,
  IAnalyticsPageView,
  IAnalyticsScreenLog,
  IAnalyticsTrackingEvent,
} from '@utils/interfaces/analytics.interface';
import { ThemeService } from './theme.service';
import { fetchSettings } from 'src/main';
import { ScriptInjectorService } from './script-injector.service';

declare let dataLayer: any;
declare global {
  interface Window {
    adobeDataLayer: any[];
  }
}
declare const window: Window;
declare const _satellite: any;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  idUnico = '';
  userId = '';
  analytics = { gtm: '' };

  constructor(private themeService: ThemeService, private scriptInjectorService: ScriptInjectorService) {}

  initAdobeLaunchDataLayer() {
    (window as Window).adobeDataLayer = [];
  }

  async injectAdobeLaunchScript() {
    const config = await fetchSettings();
    try {
      await this.scriptInjectorService.load(
        'launch',
        `https://assets.adobedtm.com/232caedc0ec1/69b36b1ef5df/launch-${config.analytics_adobe}.min.js`
      );
      _satellite.pageBottom();
    } catch (e) {
      console.error('Erro ao carregar Adobe Launch script', e);
    }
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  setIdUnico(idUnico: string) {
    this.idUnico = idUnico;
  }

  /**
   * Retorna true se o dataLayer existe
   */
  private useGtm(): boolean {
    return this.analytics && this.analytics.gtm && typeof dataLayer !== undefined;
  }

  /**
   * Dispara evento screen_view para o Google Analytics Tag Manager
   * @param pageName nome da tela
   * @param jornada nome da jornada
   */
  public async screenLog({ pageName, jornada }: { pageName: string; jornada: string }) {
    if (!this.useGtm()) {
      return;
    }

    const params: IAnalyticsScreenLog = {
      page: `${this.themeService.getTheme().sigla.toLowerCase()}:${pageName}`,
      contentGrouping: jornada,
      event: 'pageview',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      userId: this.userId,
      idUnico: this.idUnico,
    };

    dataLayer.push(params);
  }

  /**
   * Dispara um evento para o Google Analytics Tag Manager
   * @param pageName nome do evento
   * @param params parâmetros do evento com base na interface IAnalyticsEventLog
   */
  public async eventLog(key: { value: string; params: IAnalyticsEvent }, dimension: object = {}) {
    if (!this.useGtm()) {
      return;
    }

    const pageName = AnalyticsEventsMap.getValue(key);
    const params = AnalyticsEventsMap.getParams(key);

    const paramsModificado: IAnalyticsEventLog = {
      ...params,
      page: `${this.themeService.getTheme().sigla.toLowerCase()}:${pageName}`,
      event: 'event',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      userId: this.userId,
      idUnico: this.idUnico,
      ...dimension,
    };

    dataLayer.push(paramsModificado);
  }

  pageView(params: IAnalyticsPageView, eVars = {}) {
    const payload: Partial<IAnalyticsBase> = {
      ...params,
      userID: this.userId,
      idUnico: this.idUnico,
      contentGroupping: 'opb:open-finance',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      ...eVars,
    };

    const payloadAdobe: Partial<IAnalyticsBase> = {
      event: params.event,
      pageName: params.page,

      userID: this.userId,
      idUnico: this.idUnico,
      contentGroupping: 'opb:open-finance',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      ...eVars,
    };

    dataLayer.push(payload);
    (window as Window).adobeDataLayer.push(payloadAdobe);
  }

  // event(params: IAnalyticsTrackingEvent, eVars = {}) {
  //   const payload: Partial<IAnalyticsBase> = {
  //     ...params,
  //     userID: this.userId,
  //     idUnico: this.idUnico,
  //     contentGroupping: 'opb:open-finance',
  //     canal: this.themeService.getTheme().sigla.toLowerCase(),
  //     ...eVars,
  //   };

    /**
   * Dispara um evento do tipo EVENT para o Google Analytics Tag Manager e Adobe
   * @param params parametros do evento com base na interface IAnalyticsTrackingEvent
   * @param eVars dados dinâmicos
   * @param eventLabelDinamico dados dinâmicos para o campo eventLabel
   */
  event(params: IAnalyticsTrackingEvent, eVars = {}, eventLabelDinamico: string = undefined) {
    const payload: Partial<IAnalyticsBase> = {
      ...params,
      userID: this.userId,
      idUnico: this.idUnico,
      contentGroupping: 'opb:open-finance',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      eventLabel: eventLabelDinamico ? eventLabelDinamico : params.eventLabel,
      ...eVars,
    };

    const payloadAdobe: Partial<IAnalyticsBase> = {
      event: params.event,
      pageName: params.page,
      eventLabel: params.eventLabel,
      eventAction: params.eventAction,
      eventCategory: params.eventCategory,

      userID: this.userId,
      idUnico: this.idUnico,
      contentGroupping: 'opb:open-finance',
      canal: this.themeService.getTheme().sigla.toLowerCase(),
      ...eVars,
    };

    dataLayer.push(payload);
    (window as Window).adobeDataLayer.push(payloadAdobe);
  }
}
